import React, { Suspense, lazy } from "react";
import {
  createNativeStackNavigator,
  NativeStackScreenProps,
} from "@react-navigation/native-stack";
import Routes from "@navigations/Routes";
import LazyLoadFallback from "@components/common/LazyLoadFallback";
import type { NativeShareParams } from "@scenes/shared-collection/NativeShare";
import { JewelryConfiguratorProvider } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { InitialUrlProvider } from "../contexts/InitialUrlContext";
import { AnimationProvider } from "../contexts/AnimationContext";

const NativeShare = lazy(() => import("@scenes/shared-collection/NativeShare"));

export type SharedCollectionStackParams = {
  [Routes.NativeShare]: NativeShareParams;
};

export type SharedCollectionNavigationProps<
  ScreenName extends keyof SharedCollectionStackParams
> = NativeStackScreenProps<SharedCollectionStackParams, ScreenName>;

const Stack = createNativeStackNavigator<SharedCollectionStackParams>();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      cacheTime: 60_000,
    },
    mutations: {
      cacheTime: 0,
    },
  },
});

const SharedCollectionNavigator = () => {
  return (
    <Suspense fallback={<LazyLoadFallback fullScreen />}>
      <QueryClientProvider client={queryClient}>
        <InitialUrlProvider>
          <AnimationProvider>
            <JewelryConfiguratorProvider>
              <Stack.Navigator
                initialRouteName={Routes.NativeShare}
                screenOptions={{
                  headerShown: false,
                  freezeOnBlur: true,
                }}
              >
                <Stack.Screen
                  name={Routes.NativeShare}
                  component={NativeShare}
                />
              </Stack.Navigator>
            </JewelryConfiguratorProvider>
          </AnimationProvider>
        </InitialUrlProvider>
      </QueryClientProvider>
    </Suspense>
  );
};

export default SharedCollectionNavigator;
